import { makeAutoObservable, runInAction } from 'mobx'
import { AxiosError } from 'axios'
import { showToast } from 'shared/ui'
import { logger } from 'shared/lib'
import { TAGS_NAME_LIMIT } from 'shared/constants/limits'
import { TagsApi } from 'entities/Tags/api/tags'
import { Tag } from 'entities/Tags/model/Tag'
import type { IResponseTag } from 'entities/Tags'

export class TagsControl {
  static FromTags(tags: Tag[]) {
    const control = new TagsControl()

    tags.forEach((tag) => control._tagsMap.set(tag.id, tag))
    control._ids = Array.from(control._tagsMap.keys())
    control._synced = true

    return control
  }

  private _synced = false
  private _tagsMap = new Map<number, Tag>()

  constructor(private _ids: number[] = []) {
    this._synced = !_ids.length

    makeAutoObservable(this)
  }

  init = (tagsResponse: IResponseTag[]) => {
    tagsResponse.forEach((tag) => this._tagsMap.set(tag.id, new Tag(tag)))
    this._ids = Array.from(this._tagsMap.keys())
    this._synced = true
  }

  get synced() {
    return this._synced
  }

  get ids() {
    return this._ids
  }

  get tags() {
    return Array.from(this._tagsMap.values())
  }

  syncIds = async () => {
    if (this._synced) return
    try {
      const tags = await TagsApi.getTagsById(this._ids)

      runInAction(() => {
        tags.forEach((item) => {
          const tagId = item.id
          const tag = new Tag(item)

          this._tagsMap.set(tagId, tag)
        })
      })
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._synced = true
      })
    }
  }

  addTag = (tag: Tag) => {
    if (this._tagsMap.has(tag.id)) return

    this._tagsMap.set(tag.id, tag)

    this._ids = Array.from(this._tagsMap.keys())
  }

  createTag = async (label: string) => {
    try {
      if (label.length > TAGS_NAME_LIMIT) {
        showToast({
          type: 'warning',
          title: 'Tag name must not be longer than 50 characters',
        })

        return
      }

      const { data } = await TagsApi.createTag({ label })
      const tag = new Tag(data)
      this.addTag(tag)

      return tag
    } catch (e) {
      logger.error(e)

      if (e instanceof AxiosError) {
        showToast({
          type: 'error',
          title: e.errors.getError('label') || 'Something went wrong. Please try again',
        })
      }
    }
  }

  removeTag = (id: number) => {
    const deleted = this._tagsMap.delete(id)

    if (deleted) this._ids = Array.from(this._tagsMap.keys())
  }

  reset = () => {
    this._ids = []
    this._synced = true

    this._tagsMap.clear()
  }
}
